import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CloseSessionRequest, SessionModel, SessionStats } from '@models/session/session.model';
import { SessionOrderModel } from '@models/session-order/session-order.model';
import { SvgComponent } from '@components/svg.component';
import { DateService } from '@services/date.service';
import { SessionService } from '@services/session.service';
import { SessionOrderService } from '@services/session-order.service';
import { TerminalService } from '@services/terminal.service';
import { AuthService } from '@services/auth.service';
import { filter, switchMap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppRoutes } from '@helpers/app-routes';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-close-session-page',
  imports: [TranslateModule, ReactiveFormsModule, SvgComponent, FormatPricePipe],
  templateUrl: './close-session-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class CloseSessionPageComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly dateService = inject(DateService);
  private readonly sessionService = inject(SessionService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly authService = inject(AuthService);
  private readonly terminalService = inject(TerminalService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected readonly date = computed(() =>
    this.dateService.format(new Date(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  );

  protected readonly sessionOrders = signal<SessionOrderModel[]>([]);

  protected readonly stats = computed<SessionStats>(() => {
    const orders = this.sessionOrders();
    return {
      passages: orders.length,
      validated: orders.filter((sessionOrder) => sessionOrder.order.status === 'valid').length,
      forced: orders.filter((sessionOrder) => sessionOrder.order.status === 'forced').length,
      collected: orders.reduce((sum, sessionOrder) => sum + sessionOrder.order.price, 0),
    };
  });

  protected readonly selectedRating = signal<String | null>(null);

  protected readonly ratings = [
    { value: 'great' },
    { value: 'good' },
    { value: 'medium' },
    { value: 'hard' },
  ];

  protected readonly form = this.fb.nonNullable.group({
    comment: [''],
  });

  constructor() {
    toObservable(this.sessionService.lastSession)
      .pipe(
        filter((session): session is SessionModel => session !== null),
        switchMap((session) => this.sessionOrderService.getSessionOrders(session.id)),
        takeUntilDestroyed(),
      )
      .subscribe((orders) => this.sessionOrders.set(orders));
  }

  protected selectRating(rating: String): void {
    this.selectedRating.set(rating);
  }

  protected cancel(): void {
    void this.router.navigate([AppRoutes.scanPage]);
  }

  protected viewOrders(): void {
    void this.router.navigate([AppRoutes.orderList]);
  }

  protected closeSession(): void {
    const terminalId = this.terminalService.currentTerminal()?.id;
    const userId = this.authService.getCurrentUser()?.id;

    if (!terminalId || !userId) return;

    const sessionId = this.sessionService.lastSession()?.id;
    if (!sessionId) return;

    const request: CloseSessionRequest = {
      id: sessionId,
      self_pos_terminal_id: terminalId,
      comment: this.form.value.comment,
      closed_by_user_id: userId,
    };

    this.sessionService
      .closeSession(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => void this.router.navigate([AppRoutes.openSession]),
        error: (error) => {
          const translatedMessage = this.translate.instant(error.message);

          if (translatedMessage !== error.message) {
            this.toastr.error(
              translatedMessage,
              this.translate.instant('session.toast.error_title'),
            );
          } else {
            this.toastr.error(
              this.translate.instant('session.toast.error_message'),
              this.translate.instant('session.toast.error_title'),
            );
          }
        },
      });
  }
}
